import { useEffect } from "react";
import type { WatchWizardInitialState } from "@/domains/watches/components/WatchCreateWizard";
import { derivePositionBasisPerShare, makeMultiLegDraftFromPricedLegs } from "@/domains/watches/lib/multiLegPosition";

import { AGING_MAX_MS, archetypeLabel, computeStaleness, directionView, parseGeneratedAt } from "./screenerLabels";
import type { MultiStrategyScreenEntry, MultiStrategyScreenRow } from "./contracts";

// The backend's own explicit customer-eligible authority-label constant
// (services/apis/domains/trade_ideas/multi_strategy_screen.py's
// `AUTHORITY_LABEL_CUSTOMER_ELIGIBLE`) -- the ONLY string value this gate
// treats as proof of customer eligibility. Duplicated here as a literal
// (rather than imported) because this frontend module must never import
// from `domains/trade_ideas` (backend) or its frontend counterpart in a way
// that would create a reverse dependency; `authority_label` already crosses
// the wire as a string on `MultiStrategyScreenEntry`, so comparing against
// this literal is the correct boundary.
const AUTHORITY_LABEL_CUSTOMER_ELIGIBLE = "CUSTOMER_ELIGIBLE";

/**
 * JS-1081 (round 3 adversarial-review FIX 3): does `entry` carry POSITIVE,
 * EXPLICIT proof of customer eligibility? Fail CLOSED -- available only when
 * `shadow` is explicitly `false` AND `customer_eligible` is explicitly `true`
 * AND `authority_label` is the exact customer-eligible constant. Any absent/
 * null field on any of the three is treated as UNVERIFIED, not as "probably
 * fine" -- the previous predicate (round 2 FIX 3) only checked for explicit
 * NEGATIVE signals (`shadow === true`, `customer_eligible === false`,
 * `authority_label === "SHADOW_RESEARCH"`), which let a row with all three
 * fields simply ABSENT (e.g. an older payload, a partially-enriched entry,
 * or any future field-omission bug) pass through as available -- absence of
 * evidence is not evidence of eligibility for a boundary this consequential.
 */
function hasVerifiedCustomerEligibility(entry: MultiStrategyScreenEntry): boolean {
  return (
    entry.shadow === false
    && entry.customer_eligible === true
    && entry.authority_label === AUTHORITY_LABEL_CUSTOMER_ELIGIBLE
  );
}

/**
 * JS-1081 (round 3 adversarial-review FIX 2): does `entry` carry a COMPLETE
 * per-leg execution-friction fill -- one covering EVERY priced leg, keyed by
 * OCC contract? Fail CLOSED -- a partial map (or no map at all) is treated
 * as "pricing unavailable," never papered over by a hidden whole-structure
 * basis fallback (see `multiLegPosition.ts`'s `makeMultiLegDraftFromPricedLegs`,
 * which now honestly returns `null` for exactly this incomplete-map case for
 * a multi-leg structure).
 */
function hasCompletePerLegFills(entry: MultiStrategyScreenEntry): boolean {
  const legs = entry.priced_legs ?? [];
  if (legs.length === 0) return false;
  const fillByContract = new Map(
    (entry.execution_friction?.legs ?? [])
      .filter((leg): leg is { contract: string; realistic_fill: string } => (
        typeof leg.contract === "string" && typeof leg.realistic_fill === "string"
      ))
      .map((leg) => [leg.contract, leg.realistic_fill]),
  );
  return legs.every((leg) => typeof leg.contract === "string" && fillByContract.has(leg.contract));
}

/**
 * JS-1081: shared "+Watch" pre-fill logic for the two Desk-idea surfaces that
 * offer it (`MultiStrategyScreenTable`'s thesis drawer and `EditionPage`'s
 * idea cards) -- factored out once so the enabled/disabled reasoning and the
 * `WatchWizardInitialState` construction is not duplicated between them
 * (NO-TECH-DEBT).
 */

// NOTE: deliberately NOT a discriminated union ({available:true} |
// {available:false; reason:string}). This project's tsconfig runs with
// "strict": false (no strictNullChecks), under which TypeScript's
// control-flow narrowing on a discriminated union inside a ternary
// (`x.available ? undefined : x.reason`) does not reliably narrow --
// `reason` a "does not exist on type" error even in the branch where it is
// present. Carrying `reason` as `string | null` on every result sidesteps
// narrowing entirely and works regardless of strictness settings.
export type DeskIdeaWatchAvailability = { available: boolean; reason: string | null };

/**
 * JS-1087: does `row` carry a RECOGNIZED direction ("BULLISH" or
 * "BEARISH")? The wire contract's `direction` field is permissive
 * (`z.string().nullish()`), so it can legitimately be null, empty, or a
 * future/unrecognized value -- fail CLOSED here exactly like
 * `hasVerifiedCustomerEligibility` above: an unknown direction is not
 * "probably bullish" or "probably bearish," it is unverified, and every
 * direction-dependent action (this "+Watch" pre-fill, ticker-context deep
 * links that carry the idea's direction, and the "Ask Claude" handoff) must
 * decline honestly rather than silently proceeding as if a real direction
 * were published. Checked by both Desk-idea surfaces
 * (`MultiStrategyScreenTable`'s thesis drawer and `EditionPage`'s idea
 * cards) via this ONE shared predicate (NO-TECH-DEBT).
 */
export function hasKnownDirection(row: Pick<MultiStrategyScreenRow, "direction">): boolean {
  return directionView(row.direction).kind !== "UNKNOWN";
}

export const DIRECTION_UNAVAILABLE_REASON = "This idea's direction is unavailable, so this action is disabled.";

/**
 * Return whether a "+Watch" button can be offered for `entry`, and if not,
 * the FIRST honest reason (checked in a sensible priority order) -- never a
 * generic "unavailable" message.
 *
 * JS-1081 (adversarial-review FIX 3, tightened to fail-CLOSED in round 3):
 * the customer-eligibility / authority gate lives HERE -- the ONE shared
 * authority boundary both Desk-idea surfaces (`MultiStrategyScreenTable`'s
 * thesis drawer and `EditionPage`'s idea cards) call through -- rather than
 * each surface applying its own ad-hoc gate. Before round 2's FIX 3, the two
 * surfaces had DIVERGED (the table hid the button for a shadow entry; the
 * edition page applied no such gate at all). Round 2's fix closed that gap
 * but was still fail-OPEN in one respect: it only rejected explicit
 * NEGATIVE signals, so a row with `shadow`/`customer_eligible`/
 * `authority_label` all simply ABSENT would still read as available. Round
 * 3 FIX 3 requires POSITIVE, EXPLICIT proof via `hasVerifiedCustomerEligibility`
 * -- any absent/null field is "unverified," a DISTINCT reason from the
 * shadow/research-only case, since the two mean different things to a
 * customer (one says "not eligible," the other says "we can't confirm
 * eligibility"). Checked FIRST, before any pricing specifics, since it is
 * the most fundamental reason (this is not a verified customer-tradeable
 * structure at all) -- disabled-with-an-honest-tooltip, exactly like every
 * other reason here, never a silently-hidden button (which would look like
 * a rendering bug rather than an explained unavailability).
 *
 * JS-1081 (round 3 FIX 2): pricing availability also fails CLOSED -- a
 * multi-leg idea without a COMPLETE per-leg execution-friction fill (one
 * covering every priced leg) is unavailable with its own honest reason,
 * never silently backed by a hidden whole-structure basis fallback (see
 * `hasCompletePerLegFills` and `multiLegPosition.ts`'s
 * `makeMultiLegDraftFromPricedLegs`).
 *
 * JS-1098 (Codex round, HIGH): a row-age gate, REUSING the same client-side
 * staleness tiering the freshness badge already computes from `generated_at`
 * (`computeStaleness` in screenerLabels.ts) -- no new backend data, no new
 * timer machinery, no per-expression `quote_valid_through` (that is a
 * separate, filed backend ticket). The legacy setups-list surface withdrew
 * Ready-actionability at a per-expression quote-expiry boundary; the Desk
 * has no such per-expression signal, so this reuses the coarser per-ROW
 * freshness signal that already drives the visible staleness badge --
 * honest, not silently permissive, and consistent with what the trader can
 * already see on the row. `rowGeneratedAt` is optional (`undefined` when a
 * caller has not threaded it through) so this stays backward compatible;
 * an ABSENT/unparseable timestamp reads as `computeStaleness`'s own
 * "unknown" tier, which this gate deliberately does NOT block on (unknown
 * age is not proof of staleness -- only the worst, EXPLICIT "stale"
 * tier -- the same "Stale · re-check" tier the badge shows -- blocks).
 * Checked LAST (after every other closed check) since it is a presentation-
 * layer honesty signal, not a data-completeness one.
 */
export function deskIdeaRowStalenessGate(rowGeneratedAt: string | null | undefined): DeskIdeaWatchAvailability | null {
  if (computeStaleness(rowGeneratedAt).tier !== "stale") return null;
  return { available: false, reason: "Prices are stale — re-check before arming a watch." };
}

export function deskIdeaWatchAvailability(
  entry: MultiStrategyScreenEntry,
  rowGeneratedAt?: string | null,
): DeskIdeaWatchAvailability {
  if (!hasVerifiedCustomerEligibility(entry)) {
    if (entry.shadow === true || entry.customer_eligible === false || entry.authority_label === "SHADOW_RESEARCH") {
      // JS-1088 (owner copy-voice rule): "shadow/research-only" is
      // implementation vocabulary (the internal authority-label register),
      // not trader-facing register. Restated as a plain fact about what the
      // idea is and is not.
      return { available: false, reason: "Internal research — not customer-eligible." };
    }
    return { available: false, reason: "This idea's customer eligibility could not be verified." };
  }
  if (!entry.exit_rule) {
    return { available: false, reason: "No stamped exit rule for this idea." };
  }
  // JS-1094 (demo blocker FIX 3): `entry.exit_rule` being a truthy OBJECT is
  // not enough -- `rule_kind`/`target_value`/`stop_value`/`dte_floor` are
  // each independently `.nullish()` on the wire (multiStrategyExitRuleShape,
  // contracts.ts), so an exit-policy stamping pass can leave `exit_rule`
  // present but with one of these four fields still null (e.g. a partial
  // stamp, or a rule_kind this policy version doesn't yet compute a
  // dte_floor for). `buildDeskIdeaWatchInitialState` below REQUIRES all four
  // (see its own identical check) before it will build a draft -- this
  // predicate must reject on the EXACT same set, or a button this function
  // reports "available" can still silently no-op at click time when the
  // builder (correctly) declines.
  if (
    entry.exit_rule.rule_kind == null
    || entry.exit_rule.target_value == null
    || entry.exit_rule.stop_value == null
    || entry.exit_rule.dte_floor == null
  ) {
    return { available: false, reason: "This idea's exit rule is incomplete." };
  }
  if (!entry.priced_legs || entry.priced_legs.length === 0) {
    return { available: false, reason: "This idea has no priced legs." };
  }
  if (!entry.strikes || entry.strikes.length === 0) {
    return { available: false, reason: "This idea has not been priced yet." };
  }
  if (!entry.execution_friction?.net_realistic_fill_per_share) {
    return { available: false, reason: "No executable fill price is available for this idea." };
  }
  if (entry.priced_legs.length > 1 && !hasCompletePerLegFills(entry)) {
    return { available: false, reason: "This idea is missing a real fill price for one or more legs." };
  }
  const stalenessGate = deskIdeaRowStalenessGate(rowGeneratedAt);
  if (stalenessGate) return stalenessGate;
  return { available: true, reason: null };
}

/**
 * Build the `WatchWizardInitialState` a "+Watch" click should navigate with,
 * or `null` when the leg-draft mapping honestly fails (defense in depth --
 * the button should already be disabled in that case via
 * `deskIdeaWatchAvailability`, but this never trusts that check alone at
 * click time).
 *
 * JS-1081 (adversarial-review FIX 1): this deliberately does NOT precompute
 * absolute `profitTarget`/`lossLimit` dollar thresholds here. Freezing them
 * at click time -- keyed to the idea's own fill price and the wizard's
 * default one-contract sizing -- would silently go stale the instant the
 * customer edits quantity or per-share basis in the wizard, no longer
 * matching "close at N times the ACTUAL entry" (the entire point of a
 * value-multiple/percent-of-credit exit rule). Instead this carries the
 * RELATIVE rule (`relativeExitRule`: rule_kind + target/stop multiples +
 * dte_floor) on `WatchWizardInitialState`; `WatchCreateWizard` recomputes
 * the absolute fields LIVE from its own current confirmed basis and
 * quantity (see its `derivePositionBasisPerShare`-keyed `useEffect`) --
 * initially from the SAME basis this draft hydrates with (so the very
 * first render already looks correct), and again on every subsequent edit.
 */
export function buildDeskIdeaWatchInitialState(
  row: Pick<MultiStrategyScreenRow, "ticker">,
  entry: MultiStrategyScreenEntry,
): WatchWizardInitialState | null {
  // JS-1081 (round 3 FIX 3): defense in depth, repeating the IDENTICAL
  // closed predicate `deskIdeaWatchAvailability` uses -- never trust the
  // disabled check alone at click time, and never a looser one here.
  if (!hasVerifiedCustomerEligibility(entry)) return null;
  const fillPricePerShare = entry.execution_friction?.net_realistic_fill_per_share;
  if (!fillPricePerShare || !entry.exit_rule || !entry.priced_legs || entry.priced_legs.length === 0) {
    return null;
  }
  // JS-1081 (round 3 FIX 2): repeats `deskIdeaWatchAvailability`'s own
  // closed per-leg-fill requirement -- never trust the disabled check alone.
  if (entry.priced_legs.length > 1 && !hasCompletePerLegFills(entry)) return null;
  const { rule_kind: ruleKind, target_value: targetValue, stop_value: stopValue, dte_floor: dteFloor } = entry.exit_rule;
  if (!ruleKind || targetValue == null || stopValue == null || dteFloor == null) return null;

  // JS-1081 (adversarial-review FIX 1 correction): the wizard's pre-filled
  // authoring screen (`isPosition && initialState`, see WatchCreateWizard.tsx)
  // shows PER-LEG premium inputs for a multi-leg draft, never the shared
  // whole-structure basis field -- so per-leg premiums must be REAL values,
  // not left empty, or the customer would see blank/uneditable-looking
  // fields and `derivePositionBasisPerShare` could never resolve a basis
  // from what's actually rendered. `execution_friction.legs[].realistic_fill`
  // is each leg's OWN real execution-friction fill (never a fabricated split
  // of the net price) -- build the contract-keyed lookup this builder needs.
  const perLegRealisticFillByContract = Object.fromEntries(
    (entry.execution_friction?.legs ?? [])
      .filter((leg): leg is { contract: string; realistic_fill: string } => (
        typeof leg.contract === "string" && typeof leg.realistic_fill === "string"
      ))
      .map((leg) => [leg.contract, leg.realistic_fill]),
  );
  const draft = makeMultiLegDraftFromPricedLegs(entry.priced_legs, fillPricePerShare, perLegRealisticFillByContract);
  if (!draft) return null;
  // The draft this pre-fill hydrates with must itself resolve a basis (the
  // idea's own priced legs are, by construction, a real priced structure) --
  // if it somehow does not, honestly decline rather than open a wizard whose
  // trigger fields can never populate.
  if (!derivePositionBasisPerShare(draft)) return null;

  // JS-1081 (adversarial-review FIX 2): `entry.ledger_id` is present ONLY
  // when the backend orchestrator CONFIRMED this entry's outcome-ledger
  // write this sweep (a real insert, or an idempotent write-once hit --
  // both are real store membership; see
  // services/apis/scripts/run_multi_strategy_screen.py's per-listing loop
  // for the exact sequencing). It is NEVER a speculatively-computed id for
  // an entry that was excluded from curation, hard-failed its own liquidity
  // gate, or whose write raised. Absent then, provenance is simply omitted
  // -- an honest pre-fill without a ledger back-link rather than a
  // dangling reference to a ledger row that was never actually written.
  return {
    goal: "position",
    draft,
    positionExitLogic: "OR",
    relativeExitRule: { ruleKind, targetValue, stopValue, dteFloor },
    ...(entry.ledger_id
      ? { provenance: { kind: "TRADE_IDEA_LEDGER" as const, ledgerId: entry.ledger_id } }
      : {}),
    sourceKind: "DESK_IDEA",
    sourceLabel: `Desk idea: ${row.ticker ?? "—"} ${archetypeLabel(entry.archetype)}`,
  };
}

const MAX_TIMEOUT_MS = 2_147_000_000; // setTimeout's signed-32-bit delay ceiling.

/**
 * JS-1098 (Codex round, HIGH): schedule exactly ONE timer to the instant a
 * row still on-screen will cross from fresh/aging into the "stale · re-check"
 * tier (`generated_at + AGING_MAX_MS`, the SAME boundary `computeStaleness`
 * already tiers against -- see screenerLabels.ts), then invalidate ONCE via
 * `onBoundaryCrossed` so a page left open past that instant re-renders with
 * the +Watch gate (`deskIdeaRowStalenessGate` above) correctly disabled
 * without requiring a manual refresh. Deliberately NOT a polling loop --
 * one row-age boundary, one timer, one invalidation, mirroring the shape of
 * the (now-retired, setup-era) `useQuoteValidityBoundary` hook this
 * supersedes for the Desk surface. A row already past the boundary, or with
 * no parseable `generated_at`, schedules nothing (the gate already reads
 * correctly on this render; there is no future crossing to wait for).
 */
export function useDeskIdeaStalenessInvalidation(
  generatedAt: string | null | undefined,
  onBoundaryCrossed: () => unknown,
) {
  const generatedMs = parseGeneratedAt(generatedAt);
  const boundaryMs = generatedMs === null ? null : generatedMs + AGING_MAX_MS;

  useEffect(() => {
    if (boundaryMs === null) return;
    const delay = boundaryMs - Date.now();
    if (delay <= 0 || delay > MAX_TIMEOUT_MS) return;
    const timer = window.setTimeout(() => {
      void onBoundaryCrossed();
    }, delay);
    return () => window.clearTimeout(timer);
    // `onBoundaryCrossed` is deliberately excluded below -- it is a stable
    // query-invalidation callback at each call site (an inline
    // `() => queryClient.invalidateQueries(...)` / `() => query.refetch()`);
    // including it would re-arm the timer every render for no boundary
    // change. Re-fires only when the boundary instant itself changes (a
    // fresh fetch with a new `generated_at`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryMs]);
}
